<template>
  <div v-if="getProjectId" id="peerReviewWrapper">
    <div class="titleRow">
      <span id="titleAndProjectName">
        <h1 id="title">Peer Review</h1>
        <span id="projectName"> {{ getProjectName }} </span>
      </span>
    </div>
    <professor-view-component v-if="getIsSupervisor"></professor-view-component>
    <div v-else>
      <div id="informationRow">
        <span class="main-color">
          {{ `${$t(localeConsts.totalPoints)}: ${getOverallTotalValue} - ${$t(localeConsts.spentPoints)}: ${getSpentPoints()}` }}
        </span>
        <span v-if="activeRoundDeadline" class="main-color">
          {{ `${$t(localeConsts.deadline)}: ${activeRoundDeadline}` }}
        </span>
        <b-button :disabled="!(checkWhetherAllTextAreasAreValid() && allPointsSpent)" @click="setSaveFlagToTrue()"> Save</b-button>
      </div>
      <peer-review-entry
        v-for="member in sortedStudents"
        :key="member.user.id"
        :title-of-the-card="member.user.id === getUserId ? $t(localeConsts.self) : member.user.first_name"
        :user-id="member.user.id"
      ></peer-review-entry>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import mapGettersMixin from '../../mixins/mapGettersMixin'
import { processPromisesMixin } from '../../mixins/processPromisesMixin'
import { teamMembersMixin } from '../../mixins/teamMembersMixin'
import { apiFetchMethodsMixin } from '../../mixins/apiFetchMethodsMixin'
import PeerReviewEntry from './PeerReviewEntry.vue'
import axios from '../../../axios'
import ProfessorViewComponent from './ProfessorView/ProfessorViewComponent.vue'
// TODO: Fix round of peer review, currently undefined. also make it inaccessible when there is no active round
export default {
  name: 'PeerReview',
  components: {
    PeerReviewEntry,
    ProfessorViewComponent
  },
  mixins: [mapGettersMixin, processPromisesMixin, teamMembersMixin, apiFetchMethodsMixin],
  data() {
    return {
      localeConsts: {
        totalPoints: 'PEERREVIEW.TOTALPOINTS',
        spentPoints: 'PEERREVIEW.SPENTPOINTS',
        deadline: 'PEERREVIEW.DEADLINE',
        self: 'PEERREVIEW.SELF'
      },
      teamMembers: [],
      fetchedData: [],
      students: [],
      supervisors: [],
      saveResponsesCount: 0,
      saveErrorsCount: 0,
      isActiveRound: false
    }
  },
  computed: {
    ...mapGetters('peerReview', ['getOverallTotalValue', 'getOverallRangeValueMap', 'getTextAreasValidObject']),
    lessCommentsThanTeamMembers() {
      return Object.keys(this.getTextAreasValidObject).length < this.students.length
    },
    allPointsSpent() {
      return this.getSpentPoints() === this.getOverallTotalValue
    },
    sortedStudents() {
      return [...this.students].sort((a, b) => {
        const aIsSelf = a.user.id === this.getUserId
        const bIsSelf = b.user.id === this.getUserId
        if (aIsSelf) return -1
        if (bIsSelf) return 1
        return 0
      })
    },
    activeRoundDeadline() {
      const now = new Date()
      const activeRound = this.fetchedPeerReviewRounds.find((round) => {
        const startDate = new Date(round.start_date)
        const endDate = new Date(round.end_date)
        return now >= startDate && now <= endDate
      })
      if (!activeRound) return null
      return new Date(activeRound.end_date).toLocaleDateString()
    }
  },

  async created() {
    this.$root.$on('peer-review-saved', this.handleSaveResponse)

    await this.fetchPeerReviewRounds()
    const now = new Date()
    this.isActiveRound = this.fetchedPeerReviewRounds.some((round) => {
      const startDate = new Date(round.start_date)
      const endDate = new Date(round.end_date)
      return now >= startDate && now <= endDate
    })

    if (!this.isActiveRound && !this.getIsSupervisor) {
      this.$router.push('/dashboard')
    }
    if (this.getProjectId && !this.getIsSupervisor) {
      try {
        const response = await axios.get('peer-reviews/', {
          params: { project_id: this.getProjectId }
        })
        this.setExistingReviews(response.data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching existing peer reviews:', error)
      }
    }
  },

  beforeDestroy() {
    this.$root.$off('peer-review-saved', this.handleSaveResponse)
  },

  methods: {
    ...mapMutations('peerReview', ['setSaveFlag', 'setExistingReviews']),
    setSaveFlagToTrue() {
      this.saveResponsesCount = 0
      this.saveErrorsCount = 0
      this.setSaveFlag(true)
    },

    handleSaveResponse(result) {
      this.saveResponsesCount++
      if (!result.success) {
        this.saveErrorsCount++
      }

      if (this.saveResponsesCount === this.students.length) {
        if (this.saveErrorsCount > 0) {
          this.$bvToast.toast('Fehler beim Speichern einiger Peer Reviews.', {
            title: 'Fehler',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true
          })
          this.setSaveFlag(false)
        } else {
          this.$bvToast.toast('Alle Peer Reviews wurden erfolgreich gespeichert.', {
            title: 'Erfolg',
            variant: 'success',
            autoHideDelay: 3000,
            solid: true
          })

          this.setSaveFlag(false)

          setTimeout(() => {
            this.$router.push('/dashboard')
          }, 1500)
        }
      }
    },

    getSpentPoints() {
      let counter = 0
      Object.keys(this.getOverallRangeValueMap).forEach((key) => {
        counter += this.getOverallRangeValueMap[key]
      })
      return counter
    },
    checkWhetherAllTextAreasAreValid() {
      let isValidBool = true
      if (this.lessCommentsThanTeamMembers) {
        isValidBool = false

        return false
      }
      Object.keys(this.getTextAreasValidObject).forEach((key) => {
        if (this.getTextAreasValidObject[key] === false) {
          isValidBool = false

          return false
        }
      })

      return isValidBool
    }
  }
}
</script>

<style>
#peerReviewWrapper {
  width: -webkit-fill-available;
}

.titleRow {
  color: #8b69a9;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

#titleAndProjectName {
  display: flex;
  align-items: baseline;
}

#title {
  color: #8b69a9;
}

#projectName {
  margin-left: 20px;
  font-size: 30px;
}

#informationRow {
  display: grid;
  grid-template-columns: 1fr auto;
  margin-bottom: 10px;
  justify-content: start;
}
</style>
